import {
    MatterInsiderEmployeePrefill,
    MatterInsiderListExport,
    MatterInsiderRow,
    MatterInsiderSave,
} from 'types/matter-insider-list';
import { objectToQueryParams } from 'utils/linkUtil';
import { portalAPI } from './portalAPI';

const APIMatterInsiderListSlice = portalAPI.enhanceEndpoints({ addTagTypes: ['MatterInsiderList'] }).injectEndpoints({
    endpoints: (builder) => ({
        // Sorted by the server: access obtained, oldest first. `asOf` (an ISO instant) asks for the list as it
        // stood at that moment.
        getMatterInsiderList: builder.query<MatterInsiderRow[], { matterId: string; asOf?: string }>({
            query: ({ matterId, asOf }) => `/v1/matters/${matterId}/insider-list${objectToQueryParams({ asOf })}`,
            providesTags: (_result, _error, { matterId }) => [{ type: 'MatterInsiderList', id: matterId }],
        }),
        // The languages the Excel export has labels in.
        getMatterInsiderListExportLanguages: builder.query<string[], string>({
            query: (matterId) => `/v1/matters/${matterId}/insider-list/export/languages`,
        }),
        getMatterInsiderListExport: builder.query<
            MatterInsiderListExport,
            { matterId: string; langCode: string; asOf?: string }
        >({
            query: ({ matterId, langCode, asOf }) =>
                `/v1/matters/${matterId}/insider-list/export${objectToQueryParams({ langCode, asOf })}`,
            keepUnusedDataFor: 0,
        }),
        // Dropped as soon as the dialog closes, so an address added through it is on offer next time.
        getMatterInsiderEmployeePrefill: builder.query<
            MatterInsiderEmployeePrefill,
            { matterId: string; employeeId: number; date?: string }
        >({
            query: ({ matterId, employeeId, date }) =>
                `/v1/matters/${matterId}/insider-list/employee-prefill/${employeeId}${objectToQueryParams({ date })}`,
            keepUnusedDataFor: 0,
        }),
        addMatterInsider: builder.mutation<MatterInsiderRow, { matterId: string; body: MatterInsiderSave }>({
            query: ({ matterId, body }) => ({
                url: `/v1/matters/${matterId}/insider-list`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_result, _error, { matterId }) => [{ type: 'MatterInsiderList', id: matterId }],
        }),
        updateMatterInsider: builder.mutation<
            MatterInsiderRow,
            { matterId: string; id: number; body: MatterInsiderSave }
        >({
            query: ({ matterId, id, body }) => ({
                url: `/v1/matters/${matterId}/insider-list/${id}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (_result, _error, { matterId }) => [{ type: 'MatterInsiderList', id: matterId }],
        }),
        // Copies the employee's current profile onto their row.
        refreshMatterInsider: builder.mutation<MatterInsiderRow, { matterId: string; id: number }>({
            query: ({ matterId, id }) => ({
                url: `/v1/matters/${matterId}/insider-list/${id}/refresh-from-profile`,
                method: 'POST',
            }),
            invalidatesTags: (_result, _error, { matterId }) => [{ type: 'MatterInsiderList', id: matterId }],
        }),
        deleteMatterInsider: builder.mutation<void, { matterId: string; id: number }>({
            query: ({ matterId, id }) => ({
                url: `/v1/matters/${matterId}/insider-list/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { matterId }) => [{ type: 'MatterInsiderList', id: matterId }],
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetMatterInsiderListQuery,
    useGetMatterInsiderListExportLanguagesQuery,
    useLazyGetMatterInsiderListExportQuery,
    useGetMatterInsiderEmployeePrefillQuery,
    useLazyGetMatterInsiderEmployeePrefillQuery,
    useAddMatterInsiderMutation,
    useUpdateMatterInsiderMutation,
    useRefreshMatterInsiderMutation,
    useDeleteMatterInsiderMutation,
} = APIMatterInsiderListSlice;
